import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Home, Phone, AlertCircle, CheckCircle, DollarSign, MessageCircle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const POPULAR_BRANDS_QUICK = [
  "Dexcom G7 & G6", "FreeStyle Libre 3 / 2 / 14-Day",
  "Omnipod 5, DASH & Classic", "Medtronic 780G & Guardian",
  "Tandem t:slim", "Accu-Chek, OneTouch, Contour Next",
  "ReliOn, True Metrix, FreeStyle", "Insulin (Humulin, Novolin)",
];

const SERVICE_AREAS = [
  "Jacksonville", "Jacksonville Beach", "Orange Park",
  "Green Cove Springs", "Clay County", "St. Augustine",
  "Fernandina Beach", "Amelia Island", "Yulee",
];

export default function NotFound() {
  useEffect(() => {
    document.title = "404 — Page Not Found | CashForTestStripsJax";

    const metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);

    const metaStatus = document.createElement("meta");
    metaStatus.httpEquiv = "Status";
    metaStatus.content = "404 Not Found";
    document.head.appendChild(metaStatus);

    const backend = process.env.REACT_APP_BACKEND_URL;
    if (backend) {
      fetch(`${backend}/api/404?path=${encodeURIComponent(window.location.pathname)}`).catch(() => {});
    }

    return () => {
      metaRobots.remove();
      metaStatus.remove();
      document.title = "CashForTestStripsJax";
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex flex-col" data-testid="not-found-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="container-custom h-16 md:h-20 flex items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="not-found-logo-link">
            <div className="w-10 h-10 bg-[#0F5132] rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg text-[#1F2937]" style={{ fontFamily: "Manrope" }}>
              CashForTestStripsJax
            </span>
          </Link>
        </div>
      </header>

      {/* Main 404 block */}
      <main className="flex-1 py-12 md:py-20">
        <div className="container-custom max-w-5xl">
          {/* Error message */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#EA580C]/10 rounded-2xl mb-8">
              <AlertCircle className="w-10 h-10 text-[#EA580C]" />
            </div>
            <p className="text-7xl md:text-8xl font-bold text-[#0F5132] mb-4" style={{ fontFamily: "Manrope" }}>404</p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              Page Not Found
            </h1>
            <p className="text-lg text-[#4B5563] mb-8 max-w-2xl mx-auto leading-relaxed">
              The page you're looking for doesn't exist or has been moved. Don't worry — you can still sell your diabetic
              supplies for instant cash in Jacksonville. Pick an option below to get back on track.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" data-testid="not-found-home-link">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg w-full sm:w-auto" data-testid="not-found-home-btn">
                  <Home className="w-5 h-5 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <a href="tel:+19049559646" className="w-full sm:w-auto" data-testid="not-found-phone-link">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-2 border-[#0F5132] text-[#0F5132] hover:bg-[#0F5132]/5 w-full sm:w-auto" data-testid="not-found-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call (904) 955-9646
                </Button>
              </a>
              <a href="sms:+19049559646" className="w-full sm:w-auto" data-testid="not-found-sms-link">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-2 border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C]/5 w-full sm:w-auto" data-testid="not-found-text-btn">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Text Us
                </Button>
              </a>
            </div>
          </div>

          {/* Quick links / popular sections */}
          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100 mb-10">
            <h2 className="text-2xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>
              While you're here — quick links
            </h2>
            <p className="text-[#4B5563] mb-8">
              Explore the most popular sections of our site to get the cash offer you're looking for.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/" className="block p-5 rounded-xl border border-gray-100 hover:border-[#0F5132] hover:shadow-md transition-all group" data-testid="quicklink-products">
                <Package className="w-7 h-7 text-[#0F5132] mb-3" />
                <h3 className="font-semibold text-[#1F2937] mb-1" style={{ fontFamily: "Manrope" }}>Products We Buy</h3>
                <p className="text-sm text-[#4B5563]">Dexcom, Omnipod, Libre, Medtronic & more</p>
              </Link>
              <Link to="/" className="block p-5 rounded-xl border border-gray-100 hover:border-[#0F5132] hover:shadow-md transition-all group" data-testid="quicklink-how">
                <DollarSign className="w-7 h-7 text-[#0F5132] mb-3" />
                <h3 className="font-semibold text-[#1F2937] mb-1" style={{ fontFamily: "Manrope" }}>How It Works</h3>
                <p className="text-sm text-[#4B5563]">Text, meet up, get paid same day</p>
              </Link>
              <Link to="/" className="block p-5 rounded-xl border border-gray-100 hover:border-[#0F5132] hover:shadow-md transition-all group" data-testid="quicklink-areas">
                <MapPin className="w-7 h-7 text-[#0F5132] mb-3" />
                <h3 className="font-semibold text-[#1F2937] mb-1" style={{ fontFamily: "Manrope" }}>Service Areas</h3>
                <p className="text-sm text-[#4B5563]">All of Jacksonville and surrounding areas</p>
              </Link>
              <Link to="/" className="block p-5 rounded-xl border border-gray-100 hover:border-[#0F5132] hover:shadow-md transition-all group" data-testid="quicklink-faq">
                <CheckCircle className="w-7 h-7 text-[#0F5132] mb-3" />
                <h3 className="font-semibold text-[#1F2937] mb-1" style={{ fontFamily: "Manrope" }}>FAQ</h3>
                <p className="text-sm text-[#4B5563]">Pricing, payment, legality & more</p>
              </Link>
            </div>
          </section>

          {/* About the business */}
          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100 mb-10">
            <h2 className="text-2xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              We Buy Diabetic Supplies in Jacksonville
            </h2>
            <div className="space-y-4 text-[#4B5563] leading-relaxed">
              <p>
                CashForTestStripsJacksonville.com is a locally-owned Jacksonville, FL business that purchases unused,
                unexpired diabetic test strips and supplies for instant cash. Whether you have extra Dexcom G7 sensors,
                Omnipod insulin pumps, FreeStyle Libre CGMs, Medtronic supplies, or test strips from brands like
                Accu-Chek, OneTouch, and Contour Next — we buy them all.
              </p>
              <p>
                Our mission is simple: provide an ethical way for you to recoup costs on surplus supplies while making
                diabetic care more affordable for the local community. We come to you with same-day mobile meetups at
                safe public locations — Wawa, Starbucks, grocery store parking lots — anywhere convenient throughout the
                Jacksonville metro area.
              </p>
              <p>
                Text us a photo of your supplies or call <a href="tel:+19049559646" className="text-[#0F5132] font-semibold underline">(904) 955-9646</a> for
                an instant quote. We pay cash on the spot. No shipping, no waiting, no hassle.
              </p>
            </div>
          </section>

          {/* Brand chips */}
          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100 mb-10">
            <h2 className="text-2xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>
              Brands We Accept
            </h2>
            <p className="text-[#4B5563] mb-6">
              We purchase a wide variety of diabetic supplies in good condition with at least 6 months until expiration.
              No Medicaid or Medicare-labeled boxes.
            </p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_BRANDS_QUICK.map((brand) => (
                <span key={brand} className="inline-flex items-center gap-1.5 bg-[#0F5132]/5 text-[#0F5132] px-4 py-2 rounded-full text-sm font-medium" data-testid={`brand-chip-${brand.substring(0, 10).replace(/\s/g, '-').toLowerCase()}`}>
                  <CheckCircle className="w-3.5 h-3.5" />
                  {brand}
                </span>
              ))}
            </div>
          </section>

          {/* Service area chips */}
          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100 mb-10">
            <h2 className="text-2xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>
              We Serve All of Jacksonville
            </h2>
            <p className="text-[#4B5563] mb-6">
              Same-day mobile meetups available throughout Northeast Florida. We come to you.
            </p>
            <div className="flex flex-wrap gap-2">
              {SERVICE_AREAS.map((area) => (
                <span key={area} className="inline-flex items-center gap-1.5 bg-[#F5F5F0] text-[#1F2937] px-4 py-2 rounded-full text-sm font-medium" data-testid={`area-chip-${area.replace(/\s/g, '-').toLowerCase()}`}>
                  <MapPin className="w-3.5 h-3.5 text-[#EA580C]" />
                  {area}
                </span>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="bg-[#0F5132] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>
              Ready to Get Paid for Your Test Strips?
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Skip the broken link — just call or text us a photo of your supplies and get a fast quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+19049559646" data-testid="cta-call-link">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg w-full sm:w-auto" data-testid="cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call (904) 955-9646
                </Button>
              </a>
              <Link to="/" data-testid="cta-home-link">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#0F5132] w-full sm:w-auto" data-testid="cta-home-btn">
                  Browse Homepage
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#1F2937]">
        <div className="container-custom text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} cashforteststripsjacksonville.com — Locally owned in Jacksonville, FL
          </p>
        </div>
      </footer>
    </div>
  );
}
